<template>
  <modal-wrapper v-model="showModal">
    <template #default="{ dismiss, setDirty }">
      <schema-converter-wrapper
        :legacy-schema="vacancyRequestSchema"
        :initial-form-data="initialFormData"
        :values-source="$options.valuesSource"
        v-bind="$attrs"
      >
        <template #default="{ schema, uiSchema, formData }">
          <popup-layout :title="modalTitle">
            <template #main>
              <vjsf-form
                ref="vjsf"
                :schema="schema"
                :ui-schema="uiSchema"
                :form-data="formData"
                @submit="handleSubmit"
                @dirty="setDirty"
              />
              <file-attachment-list v-model="files" :class="$style.attachments" />
            </template>
            <template #side>
              <attendee-list
                v-if="showAttendees"
                ref="attendeeList"
                v-model="attendees"
                :invalid="showAttendeeInvalid"
                :invalid-value-indices="invalidAttendeeIndices"
                :required="vacancyRequestSchema.attendee_required"
                :hint="vacancyRequestSchema.attendee_hint"
                @typed-email="handleTypedEmail"
                @invalid="handleAttendeeInvalid"
              />
            </template>
            <template #footer>
              <base-button :loading="loading" data-qa="save" @click="handleSubmitButton">
                {{ $trlMessage('button.save') }}
              </base-button>
              <base-button kind="light" data-qa="cancel" @click="dismiss">
                {{ $trlMessage('button.cancel') }}
              </base-button>
              <customize-form-button v-if="isDemo" />
            </template>
          </popup-layout>
        </template>
      </schema-converter-wrapper>
    </template>
  </modal-wrapper>
</template>

<script>
import tariffs from '@/services/tariffs';
import vacancyRequestAPI from '@/api/vacancy-request';
import ModalWrapper from '@/components/modal-next/modal-wrapper';
import PopupLayout from '@/components/popup-layout/popup-layout';
import BaseButton from '@/components/button/button';
import VjsfForm from '@/components/vjsf/vjsf-form';
import SchemaConverterWrapper from '@/components/vjsf/schema-converter-wrapper';
import FileAttachmentList from '@/components/ui/file-attachment-list/file-attachment-list';
import CustomizeFormButton from './customize-form-button';
import AttendeeList from './attendee-list';

export default {
  name: 'RequestForm',
  components: {
    AttendeeList,
    CustomizeFormButton,
    ModalWrapper,
    PopupLayout,
    BaseButton,
    SchemaConverterWrapper,
    VjsfForm,
    FileAttachmentList
  },
  inheritAttrs: false,
  model: {
    prop: 'open',
    event: 'change'
  },
  valuesSource: {},
  props: {
    open: Boolean,
    vacancyRequestSchema: {
      type: Object,
      required: true
    },
    vacancyRequest: {
      type: Object,
      default: () => ({})
    }
  },
  emits: ['change', 'updated', 'created'],
  data() {
    return {
      loading: false,
      submitted: false,
      files: [],
      attendees: [],
      typedAttendee: null,
      attendeeInvalid: false,
      serverError: null
    };
  },
  computed: {
    invalidAttendeeIndices() {
      /* ошибка от бэка приходит в формате
       * {
       *   attendees: {
       *     0: { ... }
       *     2: { ... }
       *   }
       * }
       */
      const serverAttendeesErrors = this.serverError?.attendees;
      if (!serverAttendeesErrors) {
        return [];
      }
      const result = Object.keys(serverAttendeesErrors).map(Number);
      if (result.length) {
        this.$refs.attendeeList.focus();
      }
      return result;
    },
    initialFormData() {
      return this.vacancyRequest.values ?? {};
    },
    showModal: {
      get() {
        return this.open;
      },
      set(flag) {
        this.$emit('change', flag);
      }
    },
    modalTitle() {
      return `${this.$trlMessage('vacancy_request')} ${this.vacancyRequestSchema.name}`;
    },
    isDemo() {
      return tariffs.isDemo;
    },
    showAttendees() {
      return !this.isEdit && [true, false].includes(this.vacancyRequestSchema.attendee_required);
    },
    isEdit() {
      return !!this.vacancyRequest.id;
    },
    showAttendeeInvalid() {
      return this.submitted && this.attendeeInvalid;
    }
  },
  watch: {
    vacancyRequest: {
      immediate: true,
      handler({ files, states }) {
        this.loading = false;
        this.submitted = false;
        this.typedAttendee = null;
        this.attendeeInvalid = false;

        this.files = (files ?? []).map((file) => ({
          ...file
        }));

        this.attendees = (states ?? [])
          .map((item) => ({ email: item.email, displayName: item.email, order: item.order }))
          .sort((a, b) => a.order - b.order);
      }
    }
  },
  methods: {
    handleSubmitButton() {
      this.submitted = true;
      this.$refs.vjsf.submit();
    },
    handleSubmit({ formData }) {
      this.submitted = true;
      if (!this.checkAdditionalFields()) {
        return;
      }

      formData.account_vacancy_request = this.vacancyRequestSchema.id;
      formData = this.reachedFormData(formData);

      this.serverError = null;
      this.loading = true;
      const action = this.isEdit ? this.handleUpdate : this.handleCreate;
      action(formData).finally(() => {
        this.loading = false;
        this.files = [];
      });
    },
    checkAdditionalFields() {
      if (this.attendeeInvalid) {
        this.$refs.attendeeList.focus();
        return false;
      }
      return true;
    },
    reachedFormData(formData) {
      const data = Object.assign({}, formData);
      if (this.showAttendees) {
        data.attendees = this.attendees;
        if (this.typedAttendee) {
          data.attendees.push({ email: this.typedAttendee });
        }
      }

      data.files = this.files.map(({ id }) => id);

      return data;
    },
    handleUpdate(formData) {
      return vacancyRequestAPI
        .update(this.vacancyRequest.id, formData)
        .then((vacancyRequest) => {
          this.$emit('updated', vacancyRequest);
        })
        .catch((err) => {
          this.serverError = err.response.data;
        });
    },
    handleCreate(formData) {
      return vacancyRequestAPI
        .create(formData)
        .then((vacancyRequest) => {
          this.$emit('created', vacancyRequest);
        })
        .catch((err) => {
          this.serverError = err.response.data;
        });
    },
    handleTypedEmail(email) {
      this.typedAttendee = email;
    },
    handleAttendeeInvalid(flag) {
      this.attendeeInvalid = flag;
    }
  }
};
</script>

<style lang="less" module>
.attachments {
  margin-left: -9px;
}
</style>

<i18n lang="json" />
